import { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Box, Stack } from "@mui/system";

import { Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { useTopupCompanyMutation } from "@/pages/Private/redux/companies/companies.api";

import { InputField } from "@/components/InputField/InputField";

import { Companies, TopupCompany, TopupCompanySchema } from "../schema/companies";

interface topupCompaniesModalProps {
	companies: Companies;
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const TopupCompaniesModal: FunctionComponent<topupCompaniesModalProps> = ({
	companies,
	isOpen,
	setIsOpen,
}) => {
	const { t } = useTranslation();

	const [topup, { isLoading, error }] = useTopupCompanyMutation();

	const initialValues = {
		id: companies.id,
		credits: 0,
		websiteScrapeCredits: 0,
	};

	const {
		handleSubmit,
		formState: { errors },
		setValue,
		watch,
	} = useForm<TopupCompany>({
		defaultValues: initialValues,
		resolver: zodResolver(TopupCompanySchema),
	});

	const formValues = watch();

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: TopupCompany) => {
		try {
			await topup({ ...values, id: companies.id }).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.confirm")}
			title={"Topup Credits for " + companies.name}
		>
			<Stack spacing={2}>
				<InputField
					error={!!errors.credits?.message}
					handleChange={(e) => setValue("credits", +e.target.value)}
					label={"Credits"}
					name={"credits"}
					placeholder={"Credits"}
					value={formValues.credits}
				/>
				<InputField
					error={!!errors.websiteScrapeCredits?.message}
					handleChange={(e) => setValue("websiteScrapeCredits", +e.target.value)}
					label={"Web Scrape Credits"}
					name={"websiteScrapeCredits"}
					placeholder={"1"}
					value={formValues.websiteScrapeCredits}
				/>
			</Stack>
			{getAllErrors(error, formErrors).length ? (
				<Box sx={{ mt: 2 }}>{renderErrorMessages(getAllErrors(error, formErrors))}</Box>
			) : null}
		</Modal>
	);
};
